import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  availableLanguages,
  fetchIssues,
  fetchNewsletterRecipients,
  formatIssueMonth,
  issuePath,
} from '../../lib/newsletter.js';

// Admin dashboard tab: the issue archive (drafts included) plus the list of
// opted-in recipients to paste into a mail client. Sending is manual by
// design — see newsletter/README.md.
export default function NewsletterAdmin() {
  const { t, i18n } = useTranslation();
  const [issues, setIssues] = useState(null);
  const [recipients, setRecipients] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchIssues(), fetchNewsletterRecipients()])
      .then(([rows, people]) => {
        if (cancelled) return;
        setIssues(rows);
        setRecipients(people);
      })
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
  }, []);

  const emails = (recipients ?? []).map((r) => r.email).join(', ');

  async function copyEmails() {
    try {
      await navigator.clipboard.writeText(emails);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard API unavailable (insecure context) — the textarea below
      // is still selectable, so the admin can copy by hand.
    }
  }

  if (error) {
    return (
      <p className="rounded-md border border-red-500/40 bg-red-950/30 p-3 text-sm text-red-200">{error}</p>
    );
  }
  if (!issues || !recipients) {
    return (
      <p className="rounded-lg border border-white/10 bg-navy-base/30 p-8 text-center text-ink-secondary">
        {t('common.loading')}
      </p>
    );
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
      <section>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif text-2xl text-cream">{t('admin.newsletter.issuesHeading')}</h2>
          <Link to="/newsletter/new" className="btn-primary text-sm">
            {t('newsletter.add')}
          </Link>
        </div>
        {issues.length === 0 ? (
          <p className="rounded-lg border border-white/10 bg-navy-base/30 p-8 text-center text-ink-secondary">
            {t('newsletter.empty')}
          </p>
        ) : (
          <ul className="divide-y divide-white/5 overflow-hidden rounded-lg border border-white/10 bg-navy-base/30">
            {issues.map((issue) => (
              <li key={issue.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-gold">
                    {formatIssueMonth(issue.year, issue.month, i18n.resolvedLanguage)}
                  </div>
                  <div className="truncate font-serif text-lg text-cream">{issue.title}</div>
                </div>
                <span className="text-[0.65rem] uppercase tracking-[0.12em] text-ink-muted">
                  {availableLanguages(issue).join(' · ')}
                </span>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-[0.12em] ${
                    issue.status === 'published'
                      ? 'border-emerald-500/40 bg-emerald-950/30 text-emerald-200'
                      : 'border-gold/40 bg-navy-mid/40 text-gold-light'
                  }`}
                >
                  {t(`newsletter.status.${issue.status}`)}
                </span>
                <Link to={issuePath(issue)} className="btn-ghost !px-3 !py-1.5 text-xs">
                  {t('newsletter.readIssue')}
                </Link>
                <Link to={`/newsletter/${issue.id}/edit`} className="btn-ghost !px-3 !py-1.5 text-xs">
                  {t('common.edit')}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <aside className="rounded-xl border border-gold/20 bg-navy-base/40 p-5">
        <h2 className="font-serif text-xl text-cream">{t('admin.newsletter.recipientsHeading')}</h2>
        <p className="mt-1 text-sm text-ink-secondary">
          {t('admin.newsletter.recipientsBody', { count: recipients.length })}
        </p>
        <textarea
          readOnly
          rows={8}
          value={emails}
          onFocus={(e) => e.target.select()}
          className="form-input mt-4 font-mono text-xs"
          aria-label={t('admin.newsletter.recipientsHeading')}
        />
        <div className="mt-3 flex items-center gap-3">
          <button type="button" className="btn-primary text-sm" onClick={copyEmails} disabled={!emails}>
            {t('admin.newsletter.copy')}
          </button>
          {copied && <span className="text-sm text-emerald-200">{t('admin.newsletter.copied')}</span>}
        </div>
        <p className="mt-4 text-xs text-ink-muted">{t('admin.newsletter.recipientsHint')}</p>
      </aside>
    </div>
  );
}
